'use client';

import { useCallback, useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../../components/Footer';
import PropertyCard from '../../../../components/PropertyCard';
import FilterPanel from '../../../../components/FilterPanel';
import HorizontalFilterBar from '../../../../components/HorizontalFilterBar';
import AdBanner from '../../../../components/AdBanner';
import Cookies from 'js-cookie';
import { api } from '@/lib/apiClient';
import { useSearchParams, useRouter } from 'next/navigation';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

// Mock rental properties - API ready
const mockRentalProperties = [
    {
        id: 'rent-001',
        title: '3 BHK Fully Furnished Apartment',
        price: 35000,
        priceUnit: '',
        pricePerSqft: null,
        type: 'Apartment',
        bedrooms: 3,
        bathrooms: 2,
        area: 1200,
        areaUnit: 'sqft',
        locality: 'Adyar',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600',
        imageCount: 10,
        badges: ['Furnished'],
        isNew: true,
        isVerified: true,
        isSaved: false,
        listingType: 'rent',
        furnishing: 'Furnished',
        tenantPreference: 'Family',
        availability: 'Immediate'
    },
    {
        id: 'rent-002',
        title: '2 BHK Apartment near Metro Station',
        price: 22000,
        priceUnit: '',
        pricePerSqft: null,
        type: 'Apartment',
        bedrooms: 2,
        bathrooms: 2,
        area: 950,
        areaUnit: 'sqft',
        locality: 'Velachery',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600',
        imageCount: 8,
        badges: ['Semi Furnished'],
        isNew: false,
        isVerified: true,
        isSaved: false,
        listingType: 'rent',
        furnishing: 'Semi-Furnished',
        tenantPreference: 'Bachelors/Family',
        availability: '15 Days'
    },
    {
        id: 'rent-003',
        title: '1 BHK Studio Apartment for Bachelors',
        price: 12000,
        priceUnit: '',
        pricePerSqft: null,
        type: 'Apartment',
        bedrooms: 1,
        bathrooms: 1,
        area: 550,
        areaUnit: 'sqft',
        locality: 'OMR',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=600',
        imageCount: 5,
        badges: ['Bachelor Friendly'],
        isNew: true,
        isVerified: true,
        isSaved: false,
        listingType: 'rent',
        furnishing: 'Unfurnished',
        tenantPreference: 'Bachelors',
        availability: 'Immediate'
    },
    {
        id: 'rent-004',
        title: '4 BHK Villa with Private Garden',
        price: 75000,
        priceUnit: '',
        pricePerSqft: null,
        type: 'Villa',
        bedrooms: 4,
        bathrooms: 4,
        area: 2500,
        areaUnit: 'sqft',
        locality: 'ECR',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600',
        imageCount: 15,
        badges: ['Premium', 'Furnished'],
        isNew: false,
        isVerified: true,
        isSaved: false,
        listingType: 'rent',
        furnishing: 'Furnished',
        tenantPreference: 'Company Lease',
        availability: '30 Days'
    },
    {
        id: 'rent-005',
        title: '2 BHK Flat in Gated Community',
        price: 25000,
        priceUnit: '',
        pricePerSqft: null,
        type: 'Apartment',
        bedrooms: 2,
        bathrooms: 2,
        area: 1050,
        areaUnit: 'sqft',
        locality: 'Anna Nagar',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600',
        imageCount: 7,
        badges: ['Gated Community'],
        isNew: true,
        isVerified: true,
        isSaved: false,
        listingType: 'rent',
        furnishing: 'Semi-Furnished',
        tenantPreference: 'Family',
        availability: 'Immediate'
    },
    {
        id: 'rent-006',
        title: '3 BHK Independent House',
        price: 40000,
        priceUnit: '',
        pricePerSqft: null,
        type: 'House',
        bedrooms: 3,
        bathrooms: 3,
        area: 1800,
        areaUnit: 'sqft',
        locality: 'Thoraipakkam',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=600',
        imageCount: 12,
        badges: ['Family Preferred'],
        isNew: false,
        isVerified: true,
        isSaved: false,
        listingType: 'rent',
        furnishing: 'Semi-Furnished',
        tenantPreference: 'Family',
        availability: '15 Days'
    },
];

const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'newest', label: 'Newest First' },
    { value: 'rent_low', label: 'Rent: Low to High' },
    { value: 'rent_high', label: 'Rent: High to Low' },
];

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

export default function RentListingPage() {
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('relevance');
    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [viewMode, setViewMode] = useState('grid');
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const searchParams = useSearchParams();
    const router = useRouter();
    const cityId = searchParams.get("cityId");
    const [city_name, setCityName] = useState("");
    const [localities, setLocalities] = useState("");
    const [parms, setParms] = useState("");
    const token = Cookies.get('token');
    const isLoggedIn = !!token;

    // Read localities from query params if any are present
    const localitiesParam = searchParams.get("localities");
    const initialLocalities = localitiesParam ? localitiesParam.split('-').map(num => parseInt(num, 10)) : [];

    const [localitiesIds, setLocalitiesIds] = useState(initialLocalities);

    useEffect(() => {
        fetchProperties();

    }, [searchParams]);

    useEffect(() => {
        let filtered = [...properties];

        // Property Type
        if (filters.propertyType?.length) {
            filtered = filtered.filter(p =>
                filters.propertyType.includes(p.type?.toLowerCase())
            );
        }

        // BHK
        if (filters.bhk?.length) {
            filtered = filtered.filter(p =>
                filters.bhk.includes(p.bedrooms?.toString())
            );
        }

        // Budget (IMPORTANT: price Lac/Cr mix)
        if (filters.budgetMin !== undefined) {
            filtered = filtered.filter(p => {
                let priceInLac = p.price;

                // Convert Cr → Lac
                if (p.priceUnit === 'Cr') {
                    priceInLac = p.price * 100;
                }

                return (
                    priceInLac >= filters.budgetMin &&
                    (filters.budgetMax === null || priceInLac <= filters.budgetMax)
                );
            });
        }

        // Furnishing
        if (filters.furnishing?.length) {
            filtered = filtered.filter(p => {
                const f = p.furnishing?.toLowerCase() || '';
                return filters.furnishing.some(opt => {
                    if (opt === 'semi') return f.includes('semi');
                    if (opt === 'unfurnished') return f.includes('unfurnished');
                    if (opt === 'furnished') return f.includes('furnished') && !f.includes('semi') && !f.includes('unfurnished');
                    return false;
                });
            });
        }

        // Tenant Preference
        if (filters.tenantPreference?.length) {
            filtered = filtered.filter(p => {
                const t = p.tenantPreference?.toLowerCase() || '';
                return filters.tenantPreference.some(opt => {
                    if (opt === 'bachelor') return t.includes('bachelor');
                    if (opt === 'family') return t.includes('family');
                    if (opt === 'company') return t.includes('company');
                    return false;
                });
            });
        }

        // Availability
        if (filters.availability?.length) {
            filtered = filtered.filter(p => {
                const a = p.availability?.toLowerCase() || '';
                return filters.availability.some(opt => {
                    if (opt === 'immediate') return a.includes('immediate');
                    if (opt === '15_days') return a.includes('15');
                    if (opt === '30_days') return a.includes('30');
                    return false;
                });
            });
        }

        // Posted By
        if (filters.postedBy?.length) {
            filtered = filtered.filter(p => {
                const posted = p.postedBy?.toLowerCase() || '';
                return filters.postedBy.some(opt => posted.includes(opt));
            });
        }

        setFilteredProperties(filtered);

    }, [filters, properties]);

    const fetchProperties = async () => {
        try {
            setParms(searchParams.toString());
            const localitiesArray = convertToArray(searchParams.get("localities"));
            setLocalitiesIds(localitiesArray);
            let whereClause = "listing_type=$1 and city_id=$2";
            let params = ['rent', cityId];

            const res = await api.post('selectwithout/view_home_cities', { sqlData: "*", where: "id=$1", whereData: [cityId] });
            //console.log("res.data", res.data);
            let city_name = res.data[0].name;
            setCityName(city_name);
            if (localitiesArray && localitiesArray.length > 0) {
                const res1 = await api.post('selectwithout/master_localities', {
                    sqlData: "locality_name as name",
                    where: "id = ANY($1)",
                    whereData: [localitiesArray],
                });
                if (res1.success === 1 && Array.isArray(res1.data)) {
                    let localitiesNames = "";
                    for (let i = 0; i < res1.data.length; i++) {
                        localitiesNames += res1.data[i].name + ", ";
                    }
                    setLocalities(localitiesNames);
                }
            } else {
                setLocalities("");
            }

            if (localitiesArray && localitiesArray.length > 0) {
                whereClause += " and locality_id = ANY($3)";
                params.push(localitiesArray);
            }

            whereClause += " order by created_at desc";

            const response = await api.post('selectwithout/view_home_listings',
                {
                    sqlData: "*",
                    where: whereClause,
                    whereData: params,
                });
            console.log("response.data", response.data);
            let property_card = [];
            for (let i = 0; i < response.data.length; i++) {
                property_card.push(response.data[i].property_card);
            }
            setProperties(property_card);
        } catch (error) {
            console.error('Error fetching properties:', error);
        }
    };

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        console.log('Filters changed:', newFilters);
    }, []);

    const handleSave = useCallback((propertyId) => {
        console.log('Save property:', propertyId);
    }, []);

    return (
        <>
            <Navbar />

            <main className="min-h-screen bg-gray">
                {/* Horizontal Filter Bar - Sticky with subtle backdrop */}
                <div className="hidden lg:block sticky top-16 z-40 shadow-lg shadow-primary/10">
                    <HorizontalFilterBar
                        filters={filters}
                        onChange={handleFilterChange}
                        listingType="rent"
                    />
                </div>

                {/* Header */}
                <div className="bg-gray">
                    <div className="container-custom py-4">
                        <header className="flex mt-5 flex-col md:flex-row md:items-center md:justify-between gap-3">
                            {/* Left: breadcrumb + title */}
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-5 mb-3">
                                <nav className="flex items-center gap-2 text-sm text-gray-500" aria-label="Breadcrumb">
                                    <span aria-current="page" className="text-gray-900 font-medium">
                                        <strong> Available {filteredProperties.length} Properties </strong> for Rent in {localities} {city_name}
                                    </span>
                                </nav>
                            </div>

                            {/* Right: Relevance select + view toggle */}
                            {/* <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                                <label htmlFor="sortBy" className="text-sm text-gray-600 whitespace-nowrap">Sort by:</label>
                                <div className="flex items-center gap-2">
                                    <select
                                        id="sortBy"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        aria-label="Sort properties"
                                        className="h-9 w-full md:w-auto text-sm px-2 rounded-md bg-white border border-gray-300 text-gray-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                    >
                                        {sortOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div> */}
                        </header>
                    </div>
                </div>

                {/* ---------- Listings + Right Sidebar (Buy-style layout) ---------- */}
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-4 items-start">

                        {/* Left: Property Grid/List (8 cols) */}
                        <section className="lg:col-span-8 flex flex-col gap-6" aria-labelledby="rent-list-heading">
                            <h2 id="rent-list-heading" className="sr-only">Rental property listings</h2>

                            {/* Mobile Filter Button */}
                            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-10 bg-white border-t shadow-lg p-4">
                                <button
                                    onClick={() => setIsMobileFilterOpen(true)}
                                    className="btn btn-primary w-full flex justify-between items-center"
                                >
                                    <span className="flex items-center gap-2">
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                            />
                                        </svg>
                                        Filters
                                    </span>

                                    {Object.keys(filters).length > 0 && (
                                        <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                                            {Object.keys(filters).length}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Property Cards */}
                            {filteredProperties.map((property) => (
                                <PropertyCard
                                    key={property.id}
                                    property={property}
                                    params={parms}
                                    listingType='rent'
                                    onSave={handleSave}
                                    variant="horizontal"
                                />
                            ))}

                            {/* Load More */}
                            {/* <div className="mt-8 text-center">
                                <button className="btn btn-white border border-gray-200 hover:border-primary hover:text-primary px-8 w-full sm:w-auto">
                                    Load More Properties
                                </button>
                            </div> */}
                        </section>

                        {/* Right: Ad Sidebar (4 cols) */}
                        <aside className="lg:col-span-4 hidden lg:flex flex-col gap-6 self-start sticky top-24 max-h-[calc(100vh-6rem)] overflow-auto" aria-label="Sidebar">
                            {/* Featured / Ad Widget */}
                            {/* <div className="p-6 bg-violet-50 rounded-2xl border border-violet-100">
                                <h3 className="text-lg font-bold text-violet-900 mb-2">Need Help Renting?</h3>
                                <p className="text-sm text-violet-700 mb-4">
                                    Connect with our rental experts for paperwork, shortlisting and visits.
                                </p>
                                <button className="w-full btn bg-violet-600 text-white hover:bg-violet-700 border-none" aria-label="Contact rental expert">
                                    Contact Expert
                                </button>
                            </div> */}

                            {/* Ad Banners */}

                            <AdBanner
                                type={"rent"}
                                noofads={2}
                            />

                        </aside>

                    </div>
                </div>
                {/* ---------- end Listings + Sidebar ---------- */}


                {/* Mobile Filter Modal (accessible basics) */}
                {isMobileFilterOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Filters">
                        <div
                            className="absolute inset-0 bg-black/50"
                            onClick={() => setIsMobileFilterOpen(false)}
                            aria-hidden="true"
                        />
                        <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white overflow-y-auto">
                            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                                <h3 className="text-lg font-medium">Filters</h3>
                                <button onClick={() => setIsMobileFilterOpen(false)} className="text-gray-600" aria-label="Close filters">
                                    ✕
                                </button>
                            </div>

                            <FilterPanel
                                filters={filters}
                                onChange={handleFilterChange}
                                listingType="rent"
                                isMobile={true}
                                onClose={() => setIsMobileFilterOpen(false)}
                            />
                        </div>
                    </div>
                )}
            </main>

            <Footer />
        </>
    );
}
